/*
 * Settings → Operations → Earthworks (EW-1).
 *
 * The company defaults (GET / PUT /earthworks/settings): the default ticket
 * mode, and the default holdback % a job takes when its holdback is turned on
 * with no % of its own. The holdback is named from meta.holdbackLabel
 * (Holdback in CA, Retainage in US), never a literal.
 *
 * A tenant with no row reads the API's defaults with `configured: false`, and
 * the page says so. Only changed fields are sent. Writes are admin / office
 * staff; everyone else reads with the controls disabled. A 403 is said as a
 * refusal, never as a saved toast. Numbering (prefix + next number per series)
 * is its own section, EwNumberingSection, with its own Save — admin only.
 */
import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useEwMeta, useEwSettings, useUpdateEwSettings } from '../../hooks/useEarthworks';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canAdminEw, ewRefusal, holdbackLabel, toInput } from '../../lib/earthworks';
import type { EwSettingsUpdate } from '../../types/earthworks';
import EwNumberingSection from './EwNumberingSection';

const inputCls =
  'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50 disabled:opacity-60 disabled:cursor-not-allowed';
const btnPrimary = 'flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed';

function ErrorLine({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" className="rounded-lg border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
      {message}
    </p>
  );
}

export default function EarthworksSettingsPage() {
  const { user } = useAuth();
  const canWrite = canAdminEw(user?.role);
  const query = useEwSettings();
  const catalog = useEwMeta().data?.data ?? null;
  const save = useUpdateEwSettings();
  const settings = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const failure = queryLoadFailure(query);
  const hb = holdbackLabel(meta);

  const [ticketMode, setTicketMode] = useState('');
  const [pct, setPct] = useState('');
  const [threshold, setThreshold] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (settings) {
      setTicketMode(settings.defaultTicketMode ?? '');
      setPct(toInput(settings.defaultHoldbackPct));
      setThreshold(toInput(settings.hoursMismatchThreshold ?? null));
    }
  }, [settings]);

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" aria-label="Loading earthworks settings" />
      </div>
    );
  }
  if (failure || !settings) {
    return (
      <div className="px-6 py-4">
        <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="earthworks settings" />
      </div>
    );
  }

  const initialPct = toInput(settings.defaultHoldbackPct);
  const initialThreshold = toInput(settings.hoursMismatchThreshold ?? null);
  const dirty =
    ticketMode !== (settings.defaultTicketMode ?? '') || pct.trim() !== initialPct || threshold.trim() !== initialThreshold;

  const onSave = () => {
    setError(null);
    const body: EwSettingsUpdate = {};
    if (ticketMode && ticketMode !== settings.defaultTicketMode) body.default_ticket_mode = ticketMode;
    if (pct.trim() !== initialPct) {
      const n = Number(pct.trim());
      if (pct.trim() === '' || !Number.isFinite(n) || n < 0 || n > 100) return setError(`The default ${hb.toLowerCase()} % must be a number from 0 to 100, e.g. 10.`);
      body.default_holdback_pct = n;
    }
    if (threshold.trim() !== initialThreshold) {
      const n = Number(threshold.trim());
      if (threshold.trim() === '' || !Number.isFinite(n) || n < 0 || n > 24) return setError('The hours mismatch allowance must be a number of hours from 0 to 24, e.g. 2.');
      body.hours_mismatch_threshold = n;
    }
    save.mutate(body, {
      onSuccess: () => toast.success('Earthworks settings saved'),
      onError: (err) => setError(ewRefusal(err, 'The settings could not be saved.')),
    });
  };

  return (
    <div className="min-h-full" data-testid="ew-settings-page">
      <div className="px-6 py-4 space-y-4">
        {canWrite ? (
          <div className="flex justify-end">
            <button type="button" onClick={onSave} disabled={!dirty || save.isPending} className={btnPrimary}>
              {save.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
              {save.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        ) : (
          <p className="text-xs text-fieldiq-muted" data-testid="ew-settings-readonly">
            Only an admin or office staff can change earthworks settings.
          </p>
        )}
        {!settings.configured && (
          <p className="text-xs text-fieldiq-muted" data-testid="ew-settings-defaults">
            These are the defaults. Nobody has saved earthworks settings for this company yet.
          </p>
        )}
        <ErrorLine message={error} />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <label htmlFor="ew-ticket-mode" className="text-sm font-medium text-white block mb-1">Default ticket mode</label>
            <select id="ew-ticket-mode" className={inputCls} value={ticketMode} disabled={!canWrite} onChange={(e) => setTicketMode(e.target.value)}>
              {!ticketMode && <option value="">—</option>}
              {(catalog?.ticketModes ?? (ticketMode ? [{ value: ticketMode, label: ticketMode }] : [])).map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-fieldiq-muted mt-1">Used when a customer has no setting of its own.</p>
          </div>
          <div>
            <label htmlFor="ew-holdback-pct" className="text-sm font-medium text-white block mb-1" data-testid="ew-holdback-label">
              Default {hb.toLowerCase()} %
            </label>
            <input id="ew-holdback-pct" className={inputCls} inputMode="decimal" value={pct} disabled={!canWrite} onChange={(e) => setPct(e.target.value)} />
            <p className="text-xs text-fieldiq-muted mt-1">0 to 100. A job takes this when its {hb.toLowerCase()} is on with no % of its own.</p>
          </div>
          <div>
            <label htmlFor="ew-hours-threshold" className="text-sm font-medium text-white block mb-1">Hours mismatch allowance (h)</label>
            <input id="ew-hours-threshold" className={inputCls} inputMode="decimal" value={threshold} disabled={!canWrite} onChange={(e) => setThreshold(e.target.value)} />
            <p className="text-xs text-fieldiq-muted mt-1">Employee Hours flags a day when a hand's clock-in runs more than this over their daily-log hours plus standby.</p>
          </div>
        </div>

        <EwNumberingSection role={user?.role} />
      </div>
    </div>
  );
}
